import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('albmove_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config?.skipAuthRedirect) {
      localStorage.removeItem('albmove_token');
      localStorage.removeItem('albmove_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = async (email, password) => {
  try {
    return await api.post('/admin/auth/login', { email, password }, { skipAuthRedirect: true });
  } catch (error) {
    const status = error?.response?.status;
    if (status !== 401 && status !== 403 && status !== 404) {
      throw error;
    }

    // Fallback: allow business/client login from the same login page.
    try {
      const clientResponse = await api.post('/v1/client/auth/login', { email, password }, { skipAuthRedirect: true });
      const clientData = clientResponse?.data?.client || {};
      const mappedRole = clientData.client_type === 'business' ? 'BUSINESS' : 'USER';

      return {
        data: {
          token: clientResponse?.data?.access_token,
          user: {
            id: clientData.id,
            email: clientData.email,
            full_name: clientData.full_name || clientData.business_name || clientData.email,
            role: mappedRole,
            client_type: clientData.client_type,
            business_name: clientData.business_name,
          },
        },
      };
    } catch (clientError) {
      const clientStatus = clientError?.response?.status;
      if (clientStatus === 401 || clientStatus === 403 || clientStatus === 404) {
        const normalizedError = new Error('Email ose fjalëkalim i pasaktë');
        normalizedError.response = {
          ...(clientError.response || {}),
          data: {
            ...(clientError.response?.data || {}),
            detail: 'Email ose fjalëkalim i pasaktë',
          },
        };
        throw normalizedError;
      }
      throw clientError;
    }
  }
};

export const changeStaffPassword = (data) => api.put('/auth/change-password', data, { skipAuthRedirect: true });
export const changeClientPassword = (data) => api.put('/v1/client/me/password', data, { skipAuthRedirect: true });
export const register = (data) => api.post('/auth/register', data);
export const getMe = () => api.get('/auth/me');

// Users
export const getUsers = (params) => api.get('/users', { params });
export const getUser = (id) => api.get(`/users/${id}`);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);
export const toggleUserStatus = (id, isActive) => api.put(`/users/${id}/status?is_active=${isActive}`);

// Moderators
export const getModerators = (params) => api.get('/moderators', { params });
export const getModerator = (id) => api.get(`/moderators/${id}`);
export const createModerator = (data) => api.post('/moderators', data);
export const updateModerator = (id, data) => api.put(`/moderators/${id}`, data);
export const deleteModerator = (id) => api.delete(`/moderators/${id}`);
export const deleteModeratorCascade = (id, options = {}) => api.delete(`/moderators/${id}/cascade`, { 
  data: { 
    confirm: true, 
    delete_sites: options.deleteSites ?? true,
    delete_chargers: options.deleteChargers ?? true,
    delete_clients: options.deleteClients ?? true
  } 
});
export const getModeratorStats = (id, params) => api.get(`/moderators/${id}/stats`, { params });
export const getModeratorClients = (id, params) => api.get(`/moderators/${id}/clients`, { params });
export const addModeratorClient = (id, data) => api.post(`/moderators/${id}/clients`, data);
export const resetModeratorPassword = (id, password) => api.post(`/moderators/${id}/reset-password`, { new_password: password });
export const resetModeratorClientPassword = (moderatorId, clientId, password) => api.post(`/moderators/${moderatorId}/clients/${clientId}/reset-password`, { new_password: password });
export const assignModeratorSites = (userId, siteIds) => api.post('/moderators/assign', { user_id: userId, site_ids: siteIds });
export const getModeratorSites = (userId) => api.get(`/moderators/${userId}/sites`);

// Sites
export const getSites = () => api.get('/sites');
export const getSite = (id) => api.get(`/sites/${id}`);
export const createSite = (data) => api.post('/sites', data);
export const updateSite = (id, data) => api.put(`/sites/${id}`, data);
export const deleteSite = (id) => api.delete(`/sites/${id}`);
export const getSiteModerator = (siteId) => api.get(`/sites/${siteId}/moderator`);
export const assignSiteModerator = (siteId, moderatorId) => api.post(`/sites/${siteId}/assign-moderator`, { moderator_id: moderatorId });

// Live status board (Admin / Moderator only)
export const getLiveBoardChargers = (siteId) =>
  api.get('/admin/chargers/live-board', {
    params: siteId ? { site_id: siteId } : undefined,
  });

// Chargers
export const getChargers = (siteId) => api.get('/chargers', { params: { site_id: siteId } });
export const getCharger = (id) => api.get(`/chargers/${id}`);
export const getChargerDetailed = (id) => api.get(`/chargers/${id}/detailed`);
export const createCharger = async (data) => {
  const response = await api.post('/chargers', data);
  try {
    await api.post('/ocpp/reload-chargers');
  } catch (e) {
    console.warn('Could not reload OCPP chargers:', e);
  }
  return response;
};
export const deleteCharger = (id) => api.delete(`/chargers/${id}`);

// Charger Actions
export const restartCharger = (id) => api.post(`/chargers/${id}/restart`);
export const clearChargerCache = (id) => api.post(`/chargers/${id}/clear-cache`);
export const resetChargerPlatformState = (id) => api.post(`/chargers/${id}/reset-platform-state`);
export const setChargerAvailability = (id, available, reason) => 
  api.post(`/chargers/${id}/set-availability`, { available, reason });
export const generateChargerQR = (id) => api.post(`/chargers/${id}/generate-qr`);

// Charger Remote Commands (new endpoints)
export const sendChargerRestart = (id) => api.post(`/chargers/${id}/commands/restart`);
export const sendChargerReset = (id, resetType = 'Hard', reason = '') => 
  api.post(`/chargers/${id}/commands/reset`, { reset_type: resetType, reason });
export const sendChargerAvailability = (id, available, reason = '') => 
  api.post(`/chargers/${id}/commands/set-availability`, { available, reason });
export const uploadChargerFirmware = (id, formData) =>
  api.post(`/chargers/${id}/commands/update-firmware`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

// Force Commands (Admin only - works even when offline)
export const forceChargerCommand = (id, commandType, resetType = 'Soft', connectorId = 0) => 
  api.post(`/chargers/${id}/commands/force`, { 
    command_type: commandType, 
    reset_type: resetType, 
    connector_id: connectorId 
  });
export const forceReconnect = (id) => api.post(`/chargers/${id}/commands/force-reconnect`);
export const getChargerHealth = (id) => api.get(`/admin/chargers/${id}/health`);

// Charger Detail Endpoints
export const getChargerConnectors = (id) => api.get(`/chargers/${id}/connectors`);
export const getChargerEnergy = (id, fromDate, toDate, groupBy = 'day', connectorId = null) => 
  api.get(`/chargers/${id}/energy`, { params: { from_date: fromDate, to_date: toDate, group_by: groupBy, connector_id: connectorId } });
export const getChargerKpis = (id, fromDate, toDate) => 
  api.get(`/chargers/${id}/kpis`, { params: { from_date: fromDate, to_date: toDate } });
export const getChargerSessions = (id, params) => api.get(`/chargers/${id}/sessions`, { params });
export const getChargerErrors = (id, params) => api.get(`/chargers/${id}/errors`, { params });

// OCPP
export const getOcppStatus = () => api.get('/ocpp/status');
export const reloadOcppChargers = () => api.post('/ocpp/reload-chargers');

// Connectors
export const getConnectors = (chargerId) => api.get('/connectors', { params: { charger_id: chargerId } });
export const getConnector = (id) => api.get(`/connectors/${id}`);
export const createConnector = (data) => api.post('/connectors', data);

// Wallet
export const getWallet = () => api.get('/wallet');
export const getWalletTransactions = (params) => api.get('/wallet/transactions', { params });
export const cashTopup = (userId, amount, siteId, notes, walletType = 'restricted') => api.post('/wallet/cash-topup', {
  user_id: userId,
  amount,
  site_id: siteId,
  wallet_type: walletType,
  notes,
});

// Payments
export const createStripeSession = (amount) => api.post('/payments/stripe/create-session', { amount, payment_method: 'stripe' });
export const getStripeStatus = (sessionId) => api.get(`/payments/stripe/status/${sessionId}`);

// Pricing
export const getPricing = () => api.get('/settings/pricing');
export const updatePricing = (data) => api.put('/settings/pricing', data);

// Sessions
export const getSessions = (params) => api.get('/sessions', { params });
export const getSessionsStats = (params) => api.get('/sessions/stats', { params });
export const getActiveSessionsLive = () => api.get('/sessions/active-live');
export const getSessionDetails = (id) => api.get(`/sessions/${id}/details`);
export const getSessionMetrics = (id) => api.get(`/sessions/${id}/metrics`);
export const getSessionInvoicePdf = (id) => api.get(`/sessions/${id}/invoice-pdf`, { responseType: 'blob' });
export const startSession = (connectorId) => api.post(`/sessions/start?connector_id=${connectorId}`);
export const stopSession = (sessionId) => api.post(`/sessions/${sessionId}/stop`);
export const deleteSession = (id, hard = false) => api.delete(`/sessions/${id}${hard ? '?hard=true' : ''}`);

// Remote Start/Stop Transaction (OCPP)
export const remoteStartTransaction = (chargerId, connectorId, userId) => 
  api.post('/ocpp/remote-start', { charger_id: chargerId, connector_id: connectorId, user_id: userId });
export const remoteStopTransaction = (chargerId, transactionId) => 
  api.post('/ocpp/remote-stop', { charger_id: chargerId, transaction_id: transactionId });

// Walk-in manual charge (allowlisted moderator only — billed to moderator wallet)
export const getModeratorManualChargeWallet = () =>
  api.get('/moderator/manual-charge/wallet');
export const getModeratorManualChargeActive = () =>
  api.get('/moderator/manual-charge/active');
export const startModeratorManualCharge = (data) =>
  api.post('/moderator/manual-charge/start', data);
export const stopModeratorManualCharge = (sessionId = null) =>
  api.post('/moderator/manual-charge/stop', sessionId ? { session_id: sessionId } : {});

// Get clients/users for selection
export const getClients = (params) => api.get('/clients', { params });
export const getClientsSimple = () => api.get('/clients/simple');

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');
export const getRecentSessions = (limit) => api.get('/dashboard/recent-sessions', { params: { limit } });

// Dashboard Realtime (Scoped)
export const getDashboardSummary = (fromDate, toDate) => 
  api.get('/dashboard/summary', { params: { from_date: fromDate, to_date: toDate } });
export const getDashboardKpis = (fromDate, toDate) => 
  api.get('/dashboard/kpis', { params: { from_date: fromDate, to_date: toDate } });
export const getDashboardCharts = (fromDate, toDate) => 
  api.get('/dashboard/charts', { params: { from_date: fromDate, to_date: toDate } });
export const getDashboardMap = () => api.get('/dashboard/map');

// Analytics
export const getRevenueTrend = (period = '30d') => api.get('/analytics/revenue-trend', { params: { period } });
export const getSessionHeatmap = (period = '30d') => api.get('/analytics/session-heatmap', { params: { period } });
export const getSessionDistribution = (period = '30d') => api.get('/analytics/session-distribution', { params: { period } });
export const getKpiSparklines = (period = '30d') => api.get('/analytics/kpi-sparklines', { params: { period } });

// Audit Logs
export const getAuditLogs = (params) => api.get('/audit-logs', { params });

// Notifications
export const getNotifications = (params) => api.get('/notifications', { params });
export const getUnreadNotificationCount = () => api.get('/notifications/unread-count');
export const markNotificationRead = (id) => api.post(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.post('/notifications/mark-all-read');
export const deleteNotification = (id) => api.delete(`/notifications/${id}`);
export const getNotificationPreferences = () => api.get('/notifications/preferences');
export const updateNotificationPreferences = (data) => api.put('/notifications/preferences', data);

// Reports - Legacy
export const getEnergyReport = (startDate, endDate) => api.get('/reports/energy', { params: { start_date: startDate, end_date: endDate } });

// Reports - Legacy Export
export const exportSessionsReport = (format, params) => api.get(`/reports/export/sessions?format=${format}`, { 
  params,
  responseType: format === 'json' ? 'json' : 'blob'
});
export const exportEnergyReport = (format, params) => api.get(`/reports/export/energy?format=${format}`, { 
  params,
  responseType: format === 'json' ? 'json' : 'blob'
});
export const exportChargersReport = (format) => api.get(`/reports/export/chargers?format=${format}`, {
  responseType: format === 'json' ? 'json' : 'blob'
});

// Reports - Enterprise Analytics API
export const getReportsOverview = (params) => api.get('/admin/reports/overview', { params });
export const getReportsSites = (params) => api.get('/admin/reports/sites', { params });
export const getReportsSiteDetail = (siteId, params) => api.get(`/admin/reports/sites/${siteId}`, { params });
export const getReportsChargers = (params) => api.get('/admin/reports/chargers', { params });
export const getReportsChargerDetail = (chargerId, params) => api.get(`/admin/reports/chargers/${chargerId}`, { params });
export const getReportsSessions = (params) => api.get('/admin/reports/sessions', { params });

// Reports - Enterprise Export
export const exportReportsOverviewPDF = (params) => api.get('/admin/reports/overview/export', { params, responseType: 'blob' });
export const exportReportsSessionsCSV = (params) => api.get('/admin/reports/sessions/export.csv', { params, responseType: 'blob' });
export const exportReportsSessionsPDF = (params) => api.get('/admin/reports/sessions/export.pdf', { params, responseType: 'blob' });
export const exportReportsSitesCSV = (params) => api.get('/admin/reports/sites/export.csv', { params, responseType: 'blob' });
export const exportReportsSitesPDF = (params) => api.get('/admin/reports/sites/export.pdf', { params, responseType: 'blob' });
export const exportReportsChargersCSV = (params) => api.get('/admin/reports/chargers/export.csv', { params, responseType: 'blob' });
export const exportReportsChargersPDF = (params) => api.get('/admin/reports/chargers/export.pdf', { params, responseType: 'blob' });
export const exportReportsChargerDetailPDF = (chargerId, params) => api.get(`/admin/reports/chargers/${chargerId}/export.pdf`, { params, responseType: 'blob' });
export const exportReportsSiteDetailPDF = (siteId, params) => api.get(`/admin/reports/sites/${siteId}/export.pdf`, { params, responseType: 'blob' });
export const exportAppChargesExcel = (params) => api.get('/reports/export/app-charges.xlsx', {
  params,
  responseType: 'blob',
});

// Charger Diagnostics
export const getChargerDiagnostics = (id) => api.get(`/chargers/${id}/diagnostics`);

// Charger Update
export const updateCharger = (id, data) => api.put(`/chargers/${id}`, data);

// Charger Photos
export const getChargerPhotos = (id) => api.get(`/chargers/${id}/photos`);
export const uploadChargerPhoto = (id, formData) => api.post(`/chargers/${id}/photos`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const deleteChargerPhoto = (chargerId, photoId) => api.delete(`/chargers/${chargerId}/photos/${photoId}`);
export const setChargerPhotoCover = (chargerId, photoId) => api.put(`/chargers/${chargerId}/photos/${photoId}/cover`);

// Cash Top-ups History
export const getCashTopups = (params) => api.get('/wallet/cash-topups', { params });

// Invoices
export const getInvoices = (params) => api.get('/invoices', { params });
export const deleteAdminInvoice = (invoiceId) => api.delete(`/admin/invoices/${invoiceId}`);

// ============== BUSINESS MANAGEMENT (Admin) ==============
export const creditBusiness = (data) => api.post('/admin/business/credit', data);
export const getBusinessList = (params) => api.get('/admin/business/list', { params });
export const getBusinessWallet = (businessId) => api.get(`/admin/business/${businessId}/wallet`);
export const getBusinessEmployees = (businessId) => api.get(`/admin/business/${businessId}/employees`);
export const createBusiness = (data) => api.post('/admin/business/create', data);
export const updateBusiness = (businessId, data) => api.patch(`/admin/business/${businessId}`, data);
export const assignBusinessWorker = (businessId, data) => api.post(`/admin/business/${businessId}/assign-worker`, data);
export const getBusinessDetail = (businessId) => api.get(`/admin/business/${businessId}`);
export const getBusinessSites = (businessId) => api.get(`/admin/business/${businessId}/sites`);
export const assignBusinessSites = (businessId, siteIds) => api.post(`/admin/business/${businessId}/assign-sites`, { site_ids: siteIds });
export const getBusinessInvoicesAdmin = (businessId) => api.get(`/admin/business/${businessId}/invoices`);
export const removeBusinessEmployeeAdmin = (businessId, employeeRecordId) => api.delete(`/admin/business/${businessId}/employees/${employeeRecordId}`);
export const resetBusinessPassword = (businessId, password) => api.post(`/admin/business/${businessId}/reset-password`, { new_password: password });

// ============== BUSINESS OWNER MANAGEMENT ==============
export const getBusinessEmployeesList = () => api.get('/v1/business/employees');
export const inviteBusinessEmployee = (data) => api.post('/v1/business/employees/invite', data);
export const allocateFundsToEmployee = (employeeId, data) => api.post(`/v1/business/employees/${employeeId}/allocate`, data);
export const getBusinessEmployeeDetail = (employeeId) => api.get(`/v1/business/employees/${employeeId}`);
export const updateBusinessEmployee = (employeeId, data) => api.patch(`/v1/business/employees/${employeeId}`, data);
export const removeBusinessEmployee = (employeeId) => api.delete(`/v1/business/employees/${employeeId}`);
export const getBusinessWalletSummary = () => api.get('/v1/business/wallet');
export const acceptBusinessInvite = (inviteToken) => api.post(`/v1/business/invites/${inviteToken}/accept`);
export const getPendingInvites = () => api.get('/v1/business/invites/pending');
export const getEmployeeBalance = () => api.get('/v1/business/employee/balance');
export const getEmployeeBusinesses = () => api.get('/v1/business/employee/my-businesses');
export const getBusinessSessions = (params) => api.get('/v1/business/sessions', { params });
export const getBusinessChargers = () => api.get('/v1/business/chargers');

export default api;
